<template>
    <b-modal
        :visible="visible"
        @hide="$emit('close')"
        title="Nuevo Plan de Acción Manual"
        size="lg"
        centered
        no-close-on-backdrop
    >
        <div class="plan-form">
            <b-row>
                <!-- Acción Correctiva -->
                <b-col cols="12">
                    <div class="form-group">
                        <label class="form-label required">Acción Correctiva</label>
                        <el-input
                            v-model="form.corrective_action"
                            type="textarea"
                            :rows="4"
                            placeholder="Describa la acción correctiva a implementar..."
                            maxlength="2000"
                            show-word-limit
                        />
                    </div>
                </b-col>
            </b-row>

            <b-row>
                <!-- Responsable -->
                <b-col md="6">
                    <div class="form-group">
                        <label class="form-label required">Responsable</label>
                        <el-select
                            v-model="form.responsible_id"
                            placeholder="Seleccione responsable"
                            class="w-100"
                            size="small"
                            filterable
                        >
                            <el-option
                                v-for="worker in workers"
                                :key="worker.id"
                                :label="worker.fullName || `${worker.names} ${worker.surnames}`"
                                :value="worker.id"
                            />
                        </el-select>
                    </div>
                </b-col>

                <!-- Prioridad -->
                <b-col md="6">
                    <div class="form-group">
                        <label class="form-label required">Prioridad</label>
                        <el-select
                            v-model="form.priority"
                            placeholder="Seleccione prioridad"
                            class="w-100"
                            size="small"
                        >
                            <el-option
                                v-for="priority in priorityOptions"
                                :key="priority.value"
                                :label="priority.label"
                                :value="priority.value"
                            />
                        </el-select>
                    </div>
                </b-col>
            </b-row>

            <b-row>
                <!-- Fecha de Vencimiento -->
                <b-col md="6">
                    <div class="form-group">
                        <label class="form-label">Fecha de Vencimiento</label>
                        <el-date-picker
                            v-model="form.end_date"
                            type="date"
                            placeholder="Seleccionar fecha"
                            format="dd/MM/yyyy"
                            value-format="yyyy-MM-dd"
                            size="small"
                            class="w-100"
                        />
                    </div>
                </b-col>
            </b-row>
        </div>

        <template #modal-footer>
            <b-button variant="secondary" @click="$emit('close')">
                Cancelar
            </b-button>
            <b-button 
                variant="primary" 
                @click="handleSubmit"
                :disabled="loading || !isFormValid"
            >
                <i v-if="loading" class="fa fa-spinner fa-spin mr-1"></i>
                <i v-else class="fa fa-save mr-1"></i>
                Crear Plan de Acción
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import { mapActions } from 'vuex';

export default {
    name: 'CreatePlanActionManualModal',
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        companyId: {
            type: Number,
            required: true,
        },
        workers: {
            type: Array,
            default: () => [],
        },
        priorityOptions: {
            type: Array,
            default: () => [
                { value: 'Alto', label: 'Alto' },
                { value: 'Medio', label: 'Medio' },
                { value: 'Bajo', label: 'Bajo' },
            ],
        },
        statusOptions: {
            type: Array,
            default: () => [
                { value: 'Abierto', label: 'Abierto' },
                { value: 'Proceso', label: 'En Proceso' },
                { value: 'Cerrado por verificar', label: 'Cerrado por verificar' },
                { value: 'Cerrado', label: 'Cerrado' },
            ],
        },
    },
    data() {
        return {
            loading: false,
            form: {
                corrective_action: '',
                responsible_id: null,
                priority: 'Medio',
                end_date: null,
                status: 'Abierto',
            },
        };
    },
    computed: {
        isFormValid() {
            return (
                this.form.corrective_action &&
                this.form.corrective_action.trim() !== '' &&
                this.form.responsible_id &&
                this.form.priority
            );
        },
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                this.resetForm();
            }
        },
    },
    methods: {
        ...mapActions('skudo/tracings', ['createManualActionPlan']),

        resetForm() {
            this.form = {
                corrective_action: '',
                responsible_id: null,
                priority: 'Medio',
                end_date: null,
                status: 'Abierto',
            };
        },

        async handleSubmit() {
            if (!this.isFormValid) {
                this.$alertWarning('Por favor complete todos los campos requeridos');
                return;
            }

            this.loading = true;
            try {
                const payload = {
                    company_id: this.companyId,
                    corrective_action: this.form.corrective_action,
                    responsible_id: this.form.responsible_id,
                    priority: this.form.priority,
                    end_date: this.form.end_date,
                    status: this.form.status,
                    module_origin: 'SG-SP',
                };

                const response = await this.createManualActionPlan(payload);

                if (response && response.data) {
                    this.$alertSuccess(response.message || 'Plan de acción creado correctamente');
                    this.$emit('created');
                    this.$emit('close');
                } else {
                    this.$alertError((response && response.message) || 'Error al crear el plan de acción');
                }
            } catch (error) {
                console.error('Error creating manual action plan:', error);
                this.$alertError(error.message || 'Error al crear el plan de acción');
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.plan-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 12px;
}

.form-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #374151;
}

.form-label.required::after {
    content: ' *';
    color: #dc2626;
}

.info-box {
    background: #dbeafe;
    border: 1px solid #93c5fd;
    border-radius: 6px;
    padding: 10px 12px;
    font-size: 0.85rem;
    color: #1e40af;
}

.w-100 {
    width: 100%;
}
</style>
